import {
  deleteAuditTrailDocumentComment,
  IAuditTrailComment,
  IAuditUser
} from '@root/query/auditTrail'
import React, { createElement, ReactNode, useState } from 'react'
import styled from 'styled-components'
import ReactMarkdown from 'react-markdown'
import { PermissionSet, useBusinessContext, usePermission } from '@root/context'
import { IUsershipPerson, useCurrentUser } from '@root/query'
import { Prompt } from '../Prompt'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeyForAuditTrail } from '@root/pages/DocumentPage/DocumentDetails/DocumentAuditTrail'
import toast from 'react-hot-toast'
import UserDetails from '../AuditTrailStep/ContentBlocks/Header/UserDetails'
import { visitParents } from 'unist-util-visit-parents'
import { getUserDisplayName } from '@root/utils'
import type { Element, Text } from 'hast'

interface Props {
  comment: IAuditTrailComment
  users: IUsershipPerson[]
}

export const AuditTrailComment: React.FC<Props> = ({ comment, users }) => {
  const isAdmin = usePermission(PermissionSet.ADMIN)
  const { data: currentUser } = useCurrentUser()
  const canRemoveUpdateComment =
    isAdmin || (comment.created_by?.id != null && currentUser?.id === comment.created_by.id)
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const [showDelete, setShowDelete] = useState(false)
  const queryClient = useQueryClient()

  const deleteMutation = useMutation({
    mutationFn: () => deleteAuditTrailDocumentComment({ slug, commentId: comment.id }),
    onSuccess: async () => {
      setShowDelete(false)
      await queryClient.invalidateQueries({
        queryKey: queryKeyForAuditTrail(slug, comment.document)
      })
      toast.success(t('components.auditTrail.commentDeleted'))
    },
    onError: async () => {
      setShowDelete(false)
      await queryClient.invalidateQueries({
        queryKey: queryKeyForAuditTrail(slug, comment.document)
      })
      toast.error(t('components.auditTrail.commentDeletedError'))
    }
  })

  /**
   * Find all mentions in the text and replace them with UserDetails component.
   * Return an array of strings and UserDetails components.
   */
  const findUserMentions = (text: string): Array<string | ReactNode> => {
    const mentionRegex = /\((userId:\d+)\)/g
    const parts = text.split(mentionRegex)

    return parts.map((part, index) => {
      if (part === '') {
        return ''
      }
      if (index % 2 === 1) {
        const matchedId = part.match(/\d+/)
        const userId = matchedId?.[0]
        const user = users?.find(usershipPerson => usershipPerson.user_id === Number(userId))

        if (!user) {
          return part
        }

        return (
          <UserDetails
            key={`user-item-${index}`}
            user={
              {
                id: user?.id,
                name: getUserDisplayName(user),
                email: user?.email
              } as IAuditUser
            }
          ></UserDetails>
        )
      }
      return part
    })
  }

  const parseChildrenForUserMention = (
    children: ReactNode[],
    node: Element,
    inline = true
  ): ReactNode[] => {
    const components: ReactNode[] = []
    children.forEach((child, index) => {
      if (typeof child !== 'string') {
        components.push(child)
        return
      }
      const component = findUserMentions(child)
      const element = createElement(
        node.tagName,
        {
          key: `user-comment-element-${node.tagName}-${index}`,
          style: inline ? { display: 'inline' } : {}
        },
        component
      )
      components.push(element)
    })
    return components
  }

  const userMentionMatch = () => {
    return {
      'has-user-mention': ({ children, node }: { children: ReactNode[]; node: Element }) => {
        return parseChildrenForUserMention(children, node)
      },
      visited: ({ children }: { children: ReactNode[] }) => {
        return children
      }
    }
  }

  function rehypeWrapText() {
    return function wrapTextTransform(tree) {
      visitParents(tree, 'text', (node: Text, ancestors: Element[]) => {
        const lastAncestor = ancestors.at(-1)
        if (lastAncestor?.tagName !== 'visited' && lastAncestor?.tagName !== 'has-user-mention') {
          const mentionRegex = /\((userId:\d+)\)/g
          const containsMention = mentionRegex.test(node.value)

          ;(node as any).type = 'element'
          ;(node as any).tagName = containsMention ? 'has-user-mention' : 'visited'
          ;(node as any).children = [{ type: 'text', value: node.value }]
        }
      })
    }
  }

  return (
    <>
      <CommentWrapper>
        <CommentTextContainer>
          <ReactMarkdown
            rehypePlugins={[rehypeWrapText]}
            linkTarget="_blank"
            components={userMentionMatch() as Partial<import('react-markdown').Components>}
          >
            {comment.text}
          </ReactMarkdown>
        </CommentTextContainer>
        {canRemoveUpdateComment && (
          <Footer>
            <Actions>
              <button
                onClick={e => {
                  e.preventDefault()
                  setShowDelete(true)
                }}
              >
                {t('general.delete')}
              </button>
            </Actions>
          </Footer>
        )}
      </CommentWrapper>

      {/* DELETE PROMPT */}
      <Prompt
        isVisible={showDelete}
        title={t('general.areYouSure')}
        description={t('components.auditTrail.removeCommentCheck')}
        onClose={() => setShowDelete(false)}
        buttons={[
          {
            text: t('general.cancel'),
            action: () => setShowDelete(false)
          },
          {
            text: t('components.auditTrail.deleteComment'),
            intent: 'danger',
            action: () => deleteMutation.mutate(),
            showSpinner: deleteMutation.isPending,
            disabled: deleteMutation.isPending
          }
        ]}
      />
    </>
  )
}

const CommentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
  border-radius: 1rem;
  max-width: fit-content;
  min-width: 100px;
  padding: ${({ theme }) => theme.spacing.xs}rem ${({ theme }) => theme.spacing.sm}rem;

  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.md}rem;
`

const CommentTextContainer = styled.div`
  & > *:last-child {
    margin: 0;
  }
`

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  margin-top: ${({ theme }) => theme.spacing.xxs}rem;
`

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  flex-direction: row;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;

  & > button {
    background: transparent;
    outline: none;
    border: none;
    padding: 0;
    color: ${({ theme }) => theme.colors.metalGray};
    font-size: ${({ theme }) => theme.fontSize.xs}rem;
    font-weight: bold;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
`
